import axiosInstance from '@/libs/axios_config'
import { MarketplaceProviderSchema } from '@/queries/providers/schema'
import {
  PaymentCategoriesSchema,
  ProvidersSchema,
  PursesArraySchema,
} from '@/schemas/provider.schema'
import { z } from 'zod'
import { PaymentPayload } from './types/provider.types'
import { parseRequestError } from '@/common/utilities.ts'
import { PaymentStatusSchema } from '@/schemas/payments.schema.ts'

export type GetPaymentProvidersArgs = {
  program_id: number
  search?: string
  per_page?: number
  page?: number
}

export const PaginatedProvidersSchema = z.object({
  current_page: z.number(),
  per_page: z.number(),
  total: z.number(),
  data: ProvidersSchema,
})

export async function getPaymentProviders(options: GetPaymentProvidersArgs) {
  const response = await axiosInstance.get('/scholar/provider', {
    params: options,
  })

  return PaginatedProvidersSchema.parse(response.data)
}

export interface ListExpensesArgs {
  pay_provider?: 0 | 1
  reimbursement?: 0 | 1
}

export const getExpenses = async (args: ListExpensesArgs = {}) => {
  const { data } = await axiosInstance.get('/scholar/expense_types', {
    params: args,
  })
  return PaymentCategoriesSchema.parse(data)
}

const PaymentDetailsResponseSchema = z.object({
  id: z.number(),
  program_id: z.number(),
  status: PaymentStatusSchema,
  type: z.number(),
  total_amount: z.number(),
  service_fee: z.number(),
})
export type PaymentDetailsResponse = z.infer<
  typeof PaymentDetailsResponseSchema
>
export async function POSTPaymentDetails(payload: PaymentPayload) {
  try {
    const response = await axiosInstance.post('/scholar/payments', payload)
    return PaymentDetailsResponseSchema.parse(response.data)
  } catch (error) {
    throw parseRequestError(error)
  }
}

export interface GetStudentPursesArgs {
  program_id: number
  application_id: number
}

export async function getStudentPurses(args: GetStudentPursesArgs) {
  const ONLY_FUNDED_PURSES_STATUS = 1
  const { data } = await axiosInstance.get('/scholar/purses', {
    params: { ...args, is_funded: ONLY_FUNDED_PURSES_STATUS },
  })
  return PursesArraySchema.parse(data)
}

export type GetProviderByIdPayload = {
  program_id: number
  provider_id: string
}

const ServiceFeeSchema = z.object({
  service_fee: z
    .number()
    .nullable()
    .transform((val) => (val === null ? 0 : val)),
})
export type ServiceFee = z.infer<typeof ServiceFeeSchema>

export async function getProviderByid({
  program_id,
  provider_id,
}: GetProviderByIdPayload) {
  const { data } = await axiosInstance.get(`/scholar/provider/${provider_id}`, {
    params: {
      program_id: program_id,
    },
  })
  return MarketplaceProviderSchema.parse(data)
}
